/**
 * Client for the platform console's plan-change surface (phase 7, decision D1).
 *
 * Separate from `catalog-admin.ts` because these are two different acts that
 * happen to both mention plans: the catalog edits what a plan IS and costs
 * (`pricing`), this decides which plan one CUSTOMER is on (`customers`). They
 * are gated differently on purpose, and keeping them in one client file is how
 * a UI comes to call one while holding only the other's grant.
 */
import { request } from '@papan/api-client'

const BASE = '/api/admin/billing'

/** Which mechanism a plan change for this tenant would use. `provider` means
 *  the change is made at Stripe and mirrored back; `local` means it is held in
 *  `tenants.platform_plan_override` and diverges until reconciled. */
export type PlanChangePath = 'provider' | 'local'

export interface PlanPickerOption {
  id: string
  name: string
  isActive: boolean
  seatPriceMinor: number
  currency: string
  /** No Stripe price ⇒ cannot be the target of a provider-path change. */
  syncedToProvider: boolean
}

export interface CustomerPlanState {
  effective: {
    planId: string
    /** The PRESET's name — "MAX". */
    planName: string
    /**
     * What to DISPLAY — "Custom", the operator's own label, or the preset's
     * name, from `tenantPlanLabel`. Optional on the wire for the same
     * split-deploy reason as `custom` below; absent falls back to `planName`,
     * which is what this tab showed before.
     */
    label?: string
    /** Which layer decided: the subscription, a local override, or the fallback. */
    source: 'subscription' | 'override' | 'free'
  }
  /**
   * Overrides sitting ON TOP of `effective` — the tenant is on that plan but
   * does not have its numbers.
   *
   * This type carried no such field until 2026-08-18, so the tab could only
   * render the assigned plan: "Plan: MAX" on a tenant whose storage ceiling
   * was an overriding 25 GB against MAX's own 20 GB. The Configuration tab
   * said "Custom based on MAX" throughout, because it is fed the customer row.
   *
   * Optional on the wire: the SPA (CF Pages) and the API (Hetzner) deploy
   * separately, so a newer SPA against an older API is a real window. Absent
   * reads as "not custom", which is the pre-existing behaviour rather than a
   * new claim.
   */
  custom?: {
    isCustom: boolean
    /** Which families hold overrides, so copy can name them. */
    reasons: Array<'ceilings' | 'models' | 'pricing'>
  }
  override: {
    planId: string
    planName: string | null
    at: string | null
    /** Stored but NOT in force — a provider-backed subscription outranks it.
     *  A state to resolve, not an error and not normal. */
    shadowed: boolean
  } | null
  subscription: {
    id: string
    planId: string
    status: string
    pendingPlanId: string | null
    cancelAtPeriodEnd: boolean
    currentPeriodEnd: string | null
    providerBacked: boolean
  } | null
  path: PlanChangePath
  billingEnabled: boolean
  plans: PlanPickerOption[]
}

export interface PlanChangeResult {
  path: PlanChangePath
  effective: 'immediate' | 'period_end'
  /** Provider path only. */
  unchanged?: boolean
  revertedPendingChange?: boolean
  /** Local path only. */
  planId?: string
  planName?: string
  source?: string
  entitlements?: unknown
}

export const planAdminApi = {
  get: (tenantId: string) =>
    request<CustomerPlanState>(`${BASE}/tenants/${encodeURIComponent(tenantId)}/plan`),

  /** `planId: null` removes a local override; refused on the provider path,
   *  where there is nothing local to remove. */
  set: (tenantId: string, planId: string | null) =>
    request<PlanChangeResult>(`${BASE}/tenants/${encodeURIComponent(tenantId)}/plan`, {
      method: 'POST',
      body: JSON.stringify({ planId }),
    }),
}
